import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, Link, useLocation } from "wouter";
import { format, formatDistanceToNow } from "date-fns";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ExternalLink,
  FileText,
  MessageSquare,
  Loader2,
  MoreVertical,
  Pencil,
  Check,
  X,
  History,
  Save,
  CheckCircle2,
  Mail,
  Info,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getScoreBandColor } from "@/lib/score-utils";
import { useAuth } from "@/lib/auth-context";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Eye, User, AlertTriangle as AlertTriangleIcon } from "lucide-react";
import { AnimalAvatar } from "@/components/animals/animal-avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/page-header";
import { useBreadcrumbs } from "@/contexts/breadcrumb-context";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useActiveLocations } from "@/hooks/use-active-locations";
import { InteractionNoteRenderer } from "@/components/interactions/InteractionNoteRenderer";
import { usePageTitle } from "@/hooks/use-page-title";
import { getKennelStatusBadgeClass, formatKennelStatus } from "@/lib/badge-styles";

/**
 * Gate I9.2 Phase 2 (C): Parse and format EMAIL_INGEST_HOLD review reason
 * Returns { badge, description } where badge is the hold type and description is any additional text
 */
function parseEmailIngestHoldReason(reason: string | null): { 
  isEmailIngestHold: boolean; 
  description: string | null;
} {
  if (!reason) return { isEmailIngestHold: false, description: null };
  
  if (reason.startsWith("EMAIL_INGEST_HOLD:")) {
    const afterColon = reason.substring("EMAIL_INGEST_HOLD:".length).trim();
    return { 
      isEmailIngestHold: true, 
      description: afterColon || null 
    };
  }
  
  if (reason.includes("EMAIL_INGEST_HOLD")) {
    const parts = reason.split(";").map(s => s.trim());
    const otherParts = parts.filter(p => !p.includes("EMAIL_INGEST_HOLD"));
    return {
      isEmailIngestHold: true,
      description: otherParts.length > 0 ? otherParts.join("; ") : null,
    };
  }
  
  return { isEmailIngestHold: false, description: reason };
}


function getScoreBandFromCode(scoreCode: string | null | undefined): string | null {
  if (!scoreCode) return null;
  const code = scoreCode.toUpperCase();
  if (code === "1" || code === "2") return "GREEN";
  if (code === "3A" || code === "3B") return "YELLOW";
  if (code === "4A" || code === "4B" || code === "5A" || code === "5B") return "RED";
  return null;
}



interface QueueReportItem {
  id: string;
  sourceReportType: string;
  sourceReportId: string;
  reviewStatus: string;
  reviewReason: string | null;
  parsedData: {
    location?: string;
    reportDate?: string;
    animals?: unknown[];
    interactions?: unknown[];
  };
  createdAt: string;
}

interface QueueResponse {
  items: QueueReportItem[];
  pagination: {
    total: number;
  };
}

interface AcceptedState {
  reportType: string;
  location: string;
  reportDate: string;
  positionLabel: string;
}

interface EditHistoryEntry {
  field: string;
  itemIndex?: number;
  oldValue: unknown;
  newValue: unknown;
  editedAt: string;
  editedBy: string;
}

interface ParseDiagnostics {
  unknownZones?: string[];
  unknownAnimalIds?: string[];
  unknownSpeciesAnimalIds?: string[];
  capacityOverages?: Array<{
    zone: string;
    expected: number;
    actual: number;
  }>;
  [key: string]: unknown;
}

interface PendingReportData {
  id: string;
  sourceReportType: string;
  sourceReportId: string;
  reviewStatus: string;
  reviewReason: string | null;
  parsedData: {
    location?: string;
    reportDate?: string;
    animals?: Array<{
      animalId: string;
      name: string;
      zone?: string;
      species?: string;
      statusCode?: string;
      [key: string]: unknown;
    }>;
    interactions?: Array<{
      animalId: string;
      animalName?: string | null;
      species?: string | null;
      animalDbId?: string | null;
      scoreCode?: string;
      author?: string;
      rawNote?: string;
      [key: string]: unknown;
    }>;
  };
  originalParsedData: unknown;
  parseDiagnostics: ParseDiagnostics | null;
  editHistory: EditHistoryEntry[];
  createdAt: string;
  updatedAt: string;
  // Gate I9.2 Phase 2: Email ingest provenance
  sourceEmailId?: string | null;
}

function interactionNeedsReview(
  item: { animalId: string; scoreCode?: string; author?: string; [key: string]: unknown },
  unknownAnimalIds: string[],
): boolean {
  return !item.scoreCode || !item.author || unknownAnimalIds.includes(item.animalId);
}

interface EditingCell {
  itemIndex: number;
  field: string;
  value: string;
}

interface SelectedPendingInteraction {
  animalId: string;
  animalName?: string | null;
  species?: string | null;
  animalDbId?: string | null;
  scoreCode?: string;
  author?: string;
  rawNote?: string;
}

export default function ReviewDetailPage() {
  usePageTitle("Review");
  useBreadcrumbs([
    { label: "Reporting" },
    { label: "Reports", href: "/reports" },
    { label: "Review Queue", href: "/reports/review" },
  ]);
  const [, params] = useRoute("/reports/review/:reportType/:id");
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.isAdmin ?? false;
  const { locations: activeLocations } = useActiveLocations();
  
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editingLocation, setEditingLocation] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [highlightedAnimalId, setHighlightedAnimalId] = useState<string | null>(null);
  const [selectedPendingInteraction, setSelectedPendingInteraction] = useState<SelectedPendingInteraction | null>(null);
  
  // Gate I17: Drawer navigation and editing state
  const [selectedInteractionIndex, setSelectedInteractionIndex] = useState<number | null>(null);
  const [drawerEditingField, setDrawerEditingField] = useState<'scoreCode' | 'author' | 'rawNote' | null>(null);
  const [drawerEditValue, setDrawerEditValue] = useState<string>("");
  const [showDrawerUnsavedWarning, setShowDrawerUnsavedWarning] = useState(false);
  const [pendingDrawerAction, setPendingDrawerAction] = useState<'previous' | 'next' | 'close' | null>(null);

  const [showReparseConfirm, setShowReparseConfirm] = useState(false);
  const [acceptedState, setAcceptedState] = useState<AcceptedState | null>(null);
  const [queueEmptyState, setQueueEmptyState] = useState(false);
  const sessionTotalRef = useRef<number>(0);
  const sessionAcceptedCountRef = useRef<number>(0);
  const [sessionPosition, setSessionPosition] = useState<number>(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const lastHeaderRef = useRef<{ locationLabel: string; reportTypeLabel: string; reportDateFormatted: string; isAllPets: boolean; sourceReportId: string; editHistoryLength: number; reviewStatus: string } | null>(null);
  const acceptedAtIndexRef = useRef<number>(-1);

  // G3.4: Check if there are unsaved edits (cell or location currently being edited)
  const hasUnsavedEdits = editingCell !== null || editingLocation !== null;

  // Get unknown species animal IDs from diagnostics (preferred) or fallback to parsing reviewReason
  const getUnknownSpeciesAnimalIds = (reportData: PendingReportData): string[] => {
    // Prefer structured diagnostics (new reports)
    if (reportData.parseDiagnostics?.unknownSpeciesAnimalIds && reportData.parseDiagnostics.unknownSpeciesAnimalIds.length > 0) {
      return reportData.parseDiagnostics.unknownSpeciesAnimalIds;
    }
    // Legacy fallback: extract IDs from reviewReason string
    if (reportData.reviewReason) {
      const match = reportData.reviewReason.match(/A\d{6,}/g);
      if (match && match.length > 0) {
        return match;
      }
    }
    return [];
  };

  // Scroll to animal row and highlight it
  const scrollToAnimalRow = (animalId: string) => {
    // Find the table row with this animal ID
    const rows = Array.from(document.querySelectorAll('[data-animal-id]'));
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.getAttribute('data-animal-id') === animalId) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedAnimalId(animalId);
        // Clear highlight after 3 seconds
        setTimeout(() => setHighlightedAnimalId(null), 3000);
        break;
      }
    }
  };


  const reportType = params?.reportType || "";
  const reportId = params?.id || "";

  useEffect(() => {
    setAcceptedState(null);
    setQueueEmptyState(false);
  }, [reportType, reportId]);

  // G3.16e: Choose endpoint based on reportType
  const getApiEndpoint = () => {
    if (reportType === "interaction") {
      return `/api/behavior/reports/${reportId}/review`;
    }
    // Default to All Pets pending endpoint
    return `/api/reports/pending/${reportId}`;
  };

  const { data, isLoading, isError, refetch } = useQuery<PendingReportData>({
    queryKey: [reportType === "interaction" ? "/api/behavior/reports/review" : "/api/reports/pending", reportId],
    queryFn: async () => {
      const res = await fetch(getApiEndpoint(), { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch pending report");
      return res.json();
    },
    enabled: !!reportId,
  });

  useEffect(() => {
    setIsNavigating(false);
  }, [data]);

  // G3.5a: Fetch zone options for dropdown
  const { data: zonesData } = useQuery<{ ok: boolean; options: Array<{ value: string; label: string }> }>({
    queryKey: ["/api/meta/zones"],
    queryFn: async () => {
      const res = await fetch("/api/meta/zones", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch zones");
      return res.json();
    },
  });
  const zoneOptions = zonesData?.options || [];

  // Fetch animal data for kennel status when a linked interaction is selected in the drawer
  const drawerAnimalId = selectedPendingInteraction?.animalId;
  const drawerAnimalDbId = selectedPendingInteraction?.animalDbId;
  const { data: drawerAnimalData } = useQuery<{ currentStatus?: { kennelStatusNorm?: string | null; substatusNorm?: string | null; }; name?: string; [key: string]: unknown }>({
    queryKey: ["/api/animals", drawerAnimalDbId],
    queryFn: async () => {
      const res = await fetch(`/api/animals/${drawerAnimalDbId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch animal");
      return res.json();
    },
    enabled: !!(drawerAnimalId && drawerAnimalDbId),
  });

  const [, setLocation] = useLocation();

  const { data: queueData, refetch: refetchQueue } = useQuery<QueueResponse>({
    queryKey: ["/api/reports/pending", "queue-nav"],
    queryFn: async () => {
      const res = await fetch("/api/reports/pending?status=pending", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch queue");
      return res.json();
    },
  });

  const sortedQueue = useMemo(() => {
    if (!queueData?.items) return [];
    return [...queueData.items].sort((a, b) => {
      const dateA = a.parsedData?.reportDate || "";
      const dateB = b.parsedData?.reportDate || "";
      return dateA.localeCompare(dateB);
    });
  }, [queueData]);

  useEffect(() => {
    if (sortedQueue.length > 0 && sessionTotalRef.current === 0) {
      sessionTotalRef.current = sortedQueue.length;
    }
  }, [sortedQueue]);

  const currentQueueIndex = useMemo(() => {
    if (!sortedQueue.length || !reportId || !reportType) return -1;
    return sortedQueue.findIndex((item) => {
      const itemUrl = item.sourceReportType === "interaction" ? item.sourceReportId : item.id;
      return itemUrl === reportId && item.sourceReportType === reportType;
    });
  }, [sortedQueue, reportId, reportType]);

  useEffect(() => {
    if (currentQueueIndex >= 0 && sessionTotalRef.current > 0) {
      setSessionPosition(currentQueueIndex + 1 + sessionAcceptedCountRef.current);
    }
  }, [currentQueueIndex]);

  const getReviewUrl = (item: QueueReportItem) => {
    const id = item.sourceReportType === "interaction" ? item.sourceReportId : item.id;
    return `/reports/review/${item.sourceReportType}/${id}`;
  };

  const prevQueueItem = useMemo(() => {
    if (currentQueueIndex <= 0) return null;
    for (let i = currentQueueIndex - 1; i >= 0; i--) {
      if (sortedQueue[i].reviewStatus === "pending") return sortedQueue[i];
    }
    return null;
  }, [sortedQueue, currentQueueIndex]);

  const nextQueueItem = useMemo(() => {
    if (currentQueueIndex < 0 || currentQueueIndex >= sortedQueue.length - 1) return null;
    for (let i = currentQueueIndex + 1; i < sortedQueue.length; i++) {
      if (sortedQueue[i].reviewStatus === "pending") return sortedQueue[i];
    }
    return null;
  }, [sortedQueue, currentQueueIndex]);

  const nextPendingItem = useMemo(() => {
    if (!sortedQueue.length) return null;
    for (let i = (currentQueueIndex >= 0 ? currentQueueIndex + 1 : 0); i < sortedQueue.length; i++) {
      if (sortedQueue[i].reviewStatus === "pending") return sortedQueue[i];
    }
    return null;
  }, [sortedQueue, currentQueueIndex]);

  const pendingCount = useMemo(() => {
    return sortedQueue.filter((q) => q.reviewStatus === "pending").length;
  }, [sortedQueue]);

  const postAcceptNextItem = useMemo(() => {
    if (!acceptedState || !sortedQueue.length) return null;
    const startIdx = acceptedAtIndexRef.current;
    for (let i = Math.max(startIdx, 0); i < sortedQueue.length; i++) {
      if (sortedQueue[i].reviewStatus === "pending") return sortedQueue[i];
    }
    if (startIdx > 0) {
      for (let i = 0; i < startIdx; i++) {
        if (sortedQueue[i].reviewStatus === "pending") return sortedQueue[i];
      }
    }
    return null;
  }, [acceptedState, sortedQueue]);

  const handleQueueNavigate = async (item: QueueReportItem) => {
    setAcceptedState(null);
    const freshQueue = await refetchQueue();
    const freshItems = freshQueue.data?.items || [];
    const freshSorted = [...freshItems].sort((a, b) => {
      const dateA = a.parsedData?.reportDate || "";
      const dateB = b.parsedData?.reportDate || "";
      return dateA.localeCompare(dateB);
    });
    const targetId = item.sourceReportType === "interaction" ? item.sourceReportId : item.id;
    const stillExists = freshSorted.find((q) => {
      const qId = q.sourceReportType === "interaction" ? q.sourceReportId : q.id;
      return qId === targetId && q.sourceReportType === item.sourceReportType && q.reviewStatus === "pending";
    });
    if (stillExists) {
      setLocation(getReviewUrl(stillExists));
    } else {
      const targetDate = item.parsedData?.reportDate || "";
      let nextAvailable: QueueReportItem | null = null;
      for (const q of freshSorted) {
        if (q.reviewStatus !== "pending") continue;
        const qDate = q.parsedData?.reportDate || "";
        if (qDate >= targetDate) {
          nextAvailable = q;
          break;
        }
      }
      if (nextAvailable) {
        setLocation(getReviewUrl(nextAvailable));
      } else {
        setQueueEmptyState(true);
      }
    }
  };

  const handleContinueToNext = async () => {
    const freshQueue = await refetchQueue();
    const freshItems = freshQueue.data?.items || [];
    const freshSorted = [...freshItems].sort((a, b) => {
      const dateA = a.parsedData?.reportDate || "";
      const dateB = b.parsedData?.reportDate || "";
      return dateA.localeCompare(dateB);
    });
    const startIdx = acceptedAtIndexRef.current;
    let nextAvailable: QueueReportItem | null = null;
    for (let i = Math.max(startIdx, 0); i < freshSorted.length; i++) {
      if (freshSorted[i].reviewStatus === "pending") {
        nextAvailable = freshSorted[i];
        break;
      }
    }
    if (!nextAvailable && startIdx > 0) {
      for (let i = 0; i < startIdx; i++) {
        if (freshSorted[i].reviewStatus === "pending") {
          nextAvailable = freshSorted[i];
          break;
        }
      }
    }
    if (nextAvailable) {
      setAcceptedState(null);
      setLocation(getReviewUrl(nextAvailable));
    }
  };

  // G3.16e: For interaction reports, mutations use the pending_report_data.id (from data?.id)
  // For all_pets reports, they use the URL reportId directly (which is already pending_report_data.id)
  const getPendingId = () => data?.id || reportId;

  const editMutation = useMutation({
    mutationFn: async (payload: { field: string; itemIndex?: number; newValue: unknown }) => {
      return apiRequest("PATCH", `/api/reports/pending/${getPendingId()}`, payload);
    },
    onSuccess: () => {
      // Invalidate both the old and new query keys
      queryClient.invalidateQueries({ queryKey: ["/api/reports/pending", reportId] });
      queryClient.invalidateQueries({ queryKey: ["/api/reports/pending"] });
      if (reportType === "interaction") {
        queryClient.invalidateQueries({ queryKey: ["/api/behavior/reports/review", reportId] });
      }
      toast({ title: "Edit saved", description: "Your changes have been saved." });
      setEditingCell(null);
      setEditingLocation(null);
    },
    onError: (error: any) => {
      toast({
        title: "Edit failed",
        description: error?.message || "Failed to save changes",
        variant: "destructive",
      });
    },
  });

  const reparseReportMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", `/api/admin/reparse-report/${getPendingId()}`, {});
    },
    onSuccess: (responseData: { ok: boolean; result: { outcome: string } }) => {
      const outcome = responseData?.result?.outcome;
      if (outcome === "auto-accepted") {
        toast({
          title: "Report auto-accepted",
          description: "The reparse found no issues — the report has been promoted to canonical data.",
        });
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return (
              typeof key === "string" &&
              (key.startsWith("/api/reports") ||
                key.startsWith("/api/behavior/reports") ||
                key.startsWith("/api/dashboard") ||
                key.startsWith("/api/population") ||
                key.startsWith("/api/animals"))
            );
          },
        });
        setLocation("/reports/review");
      } else {
        toast({
          title: "Report reparsed",
          description: "Parsed data refreshed from the original PDF.",
        });
        queryClient.invalidateQueries({ queryKey: ["/api/reports/pending", reportId] });
        queryClient.invalidateQueries({ queryKey: ["/api/reports/pending"] });
        if (reportType === "interaction") {
          queryClient.invalidateQueries({
            queryKey: ["/api/behavior/reports/review", reportId],
          });
        }
      }
    },
    onError: (error: any) => {
      toast({
        title: "Reparse failed",
        description: error?.message || "Failed to reparse report from PDF",
        variant: "destructive",
      });
    },
  });

  const { data: metaData } = useQuery<{ ingestHoldEnabled: boolean; ingestAcceptanceDisabled: boolean }>({
    queryKey: ["/api/app-metadata"],
  });
  const acceptanceDisabled = metaData?.ingestAcceptanceDisabled ?? false;

  const acceptMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", `/api/reports/pending/${getPendingId()}/accept`, {});
    },
    onSuccess: (_responseData: { ok: boolean; redirectTo: string }) => {
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && (
            key.startsWith('/api/reports') ||
            key.startsWith('/api/behavior/reports') ||
            key.startsWith('/api/interactions') ||
            key.startsWith('/api/dashboard') ||
            key.startsWith('/api/population') ||
            key.startsWith('/api/animals')
          );
        }
      });
      toast({ title: "Report accepted", description: "The report has been promoted to canonical data." });
      acceptedAtIndexRef.current = currentQueueIndex;
      sessionAcceptedCountRef.current += 1;
      const pos = sessionPosition || (currentQueueIndex + 1);
      const total = sessionTotalRef.current || sortedQueue.length;
      setAcceptedState({
        reportType: data?.sourceReportType || reportType,
        location: data?.parsedData?.location || "",
        reportDate: data?.parsedData?.reportDate || "",
        positionLabel: `Reviewing report ${pos} of ${total}`,
      });
      refetchQueue();
    },
    onError: (error: any) => {
      toast({
        title: "Accept failed",
        description: error?.message || "Failed to accept report",
        variant: "destructive",
      });
    },
  });

  const handleCellEdit = (itemIndex: number, field: string, currentValue: string) => {
    setEditingCell({ itemIndex, field, value: currentValue });
  };

  const handleCellSave = () => {
    if (!editingCell) return;
    
    const arrayField = reportType === "all_pets" ? "animals" : "interactions";
    editMutation.mutate({
      field: `${arrayField}.${editingCell.field}`,
      itemIndex: editingCell.itemIndex,
      newValue: editingCell.value,
    });
  };

  // Gate I17: Computed all interactions list for drawer navigation
  const allInteractionsList = useMemo(() => {
    if (!data || reportType !== "interaction") return [];
    return data.parsedData?.interactions || [];
  }, [data, reportType]);

  const interactionUnknownAnimalIds = useMemo(() => {
    if (!data || reportType !== "interaction") return [];
    return data.parseDiagnostics?.unknownAnimalIds || [];
  }, [data, reportType]);

  const reviewIndices = useMemo(() => {
    const indices: number[] = [];
    for (let i = 0; i < allInteractionsList.length; i++) {
      if (interactionNeedsReview(allInteractionsList[i], interactionUnknownAnimalIds)) {
        indices.push(i);
      }
    }
    return indices;
  }, [allInteractionsList, interactionUnknownAnimalIds]);

  const totalInteractionCount = allInteractionsList.length;
  const reviewCount = reviewIndices.length;

  const findNextReviewIndex = (fromIndex: number): number | null => {
    for (const ri of reviewIndices) {
      if (ri > fromIndex) return ri;
    }
    return null;
  };
  const findPrevReviewIndex = (fromIndex: number): number | null => {
    for (let i = reviewIndices.length - 1; i >= 0; i--) {
      if (reviewIndices[i] < fromIndex) return reviewIndices[i];
    }
    return null;
  };

  const canGoPrevious = selectedInteractionIndex !== null && findPrevReviewIndex(selectedInteractionIndex) !== null;
  const canGoNext = selectedInteractionIndex !== null && findNextReviewIndex(selectedInteractionIndex) !== null;
  const hasDrawerUnsavedEdits = drawerEditingField !== null;

  const currentReviewPosition = useMemo(() => {
    if (selectedInteractionIndex === null) return null;
    const pos = reviewIndices.indexOf(selectedInteractionIndex);
    return pos !== -1 ? pos + 1 : null;
  }, [selectedInteractionIndex, reviewIndices]);

  // Gate I17: Sync drawer data after query refetch (only when allInteractionsList changes)
  const allInteractionsListRef = useRef(allInteractionsList);
  useEffect(() => {
    // Only sync if data actually changed (not just on index change)
    if (allInteractionsList !== allInteractionsListRef.current) {
      allInteractionsListRef.current = allInteractionsList;
      if (selectedInteractionIndex !== null && allInteractionsList.length > 0) {
        if (selectedInteractionIndex < allInteractionsList.length) {
          const freshInteraction = allInteractionsList[selectedInteractionIndex];
          setSelectedPendingInteraction(freshInteraction as SelectedPendingInteraction);
        } else {
          // Index out of bounds (data changed), close drawer
          setSelectedPendingInteraction(null);
          setSelectedInteractionIndex(null);
        }
      }
    }
  }, [allInteractionsList, selectedInteractionIndex]);

  // Gate I17: Open drawer with navigation index
  const handleOpenInteractionDrawer = (interaction: SelectedPendingInteraction, index: number) => {
    setSelectedInteractionIndex(index);
    setSelectedPendingInteraction(interaction);
    setDrawerEditingField(null);
    setDrawerEditValue("");
  };

  const navigateToReviewIndex = (newIndex: number) => {
    const interaction = allInteractionsList[newIndex];
    setSelectedInteractionIndex(newIndex);
    setSelectedPendingInteraction(interaction as SelectedPendingInteraction);
    setDrawerEditingField(null);
    setDrawerEditValue("");
  };

  const handleDrawerPrevious = () => {
    if (!canGoPrevious || selectedInteractionIndex === null) return;
    if (hasDrawerUnsavedEdits) {
      setPendingDrawerAction('previous');
      setShowDrawerUnsavedWarning(true);
      return;
    }
    const prevIdx = findPrevReviewIndex(selectedInteractionIndex);
    if (prevIdx !== null) navigateToReviewIndex(prevIdx);
  };

  const handleDrawerNext = () => {
    if (!canGoNext || selectedInteractionIndex === null) return;
    if (hasDrawerUnsavedEdits) {
      setPendingDrawerAction('next');
      setShowDrawerUnsavedWarning(true);
      return;
    }
    const nextIdx = findNextReviewIndex(selectedInteractionIndex);
    if (nextIdx !== null) navigateToReviewIndex(nextIdx);
  };

  // Gate I17: Close drawer with unsaved changes check
  const handleDrawerClose = () => {
    if (hasDrawerUnsavedEdits) {
      setPendingDrawerAction('close');
      setShowDrawerUnsavedWarning(true);
      return;
    }
    setSelectedPendingInteraction(null);
    setSelectedInteractionIndex(null);
    setDrawerEditingField(null);
    setDrawerEditValue("");
  };

  const handleDrawerDiscardAndContinue = () => {
    setDrawerEditingField(null);
    setDrawerEditValue("");
    setShowDrawerUnsavedWarning(false);
    
    if (pendingDrawerAction === 'previous' && selectedInteractionIndex !== null) {
      const prevIdx = findPrevReviewIndex(selectedInteractionIndex);
      if (prevIdx !== null) navigateToReviewIndex(prevIdx);
    } else if (pendingDrawerAction === 'next' && selectedInteractionIndex !== null) {
      const nextIdx = findNextReviewIndex(selectedInteractionIndex);
      if (nextIdx !== null) navigateToReviewIndex(nextIdx);
    } else if (pendingDrawerAction === 'close') {
      setSelectedPendingInteraction(null);
      setSelectedInteractionIndex(null);
    }
    setPendingDrawerAction(null);
  };

  // Gate I17: Start editing a drawer field
  const handleDrawerFieldEdit = (field: 'scoreCode' | 'author' | 'rawNote', currentValue: string) => {
    setDrawerEditingField(field);
    setDrawerEditValue(currentValue);
  };

  // Gate I17: Save drawer field edit
  const handleDrawerFieldSave = () => {
    if (selectedInteractionIndex === null || !drawerEditingField) return;
    
    editMutation.mutate({
      field: `interactions.${drawerEditingField}`,
      itemIndex: selectedInteractionIndex,
      newValue: drawerEditValue,
    }, {
      onSuccess: () => {
        // Update the selected interaction with new value
        if (selectedPendingInteraction) {
          setSelectedPendingInteraction({
            ...selectedPendingInteraction,
            [drawerEditingField]: drawerEditValue,
          });
        }
        setDrawerEditingField(null);
        setDrawerEditValue("");
      },
    });
  };

  // Gate I17: Cancel drawer field edit
  const handleDrawerFieldCancel = () => {
    setDrawerEditingField(null);
    setDrawerEditValue("");
  };

  const handleLocationSave = (newLocation: string) => {
    editMutation.mutate({
      field: "location",
      newValue: newLocation,
    });
  };

  const isAllPets = data ? data.sourceReportType === "all_pets" : (lastHeaderRef.current?.isAllPets ?? false);
  const items = data ? (isAllPets ? data.parsedData?.animals || [] : data.parsedData?.interactions || []) : [];

  const isSuperAdmin = user?.isSuperAdmin ?? false;

  const reparseSentinel = (() => {
    if (!data?.editHistory) return null;
    for (let i = data.editHistory.length - 1; i >= 0; i--) {
      if (data.editHistory[i].field === "__system_reparse__") return data.editHistory[i];
    }
    return null;
  })();

  const locationLabel = data
    ? (activeLocations.find(l => l.code === data.parsedData?.location)?.label || data.parsedData?.location || "Unknown")
    : (lastHeaderRef.current?.locationLabel ?? "");
  const reportDateFormatted = data?.parsedData?.reportDate 
    ? format(new Date(data.parsedData.reportDate + "T00:00:00"), "MM/dd/yyyy")
    : (lastHeaderRef.current?.reportDateFormatted ?? "");
  const reportTypeLabel = data
    ? (isAllPets ? "All Pets" : "Daily Interactions")
    : (lastHeaderRef.current?.reportTypeLabel ?? "");

  if (data) {
    lastHeaderRef.current = {
      locationLabel,
      reportTypeLabel,
      reportDateFormatted,
      isAllPets,
      sourceReportId: data.sourceReportId,
      editHistoryLength: data.editHistory?.length || 0,
      reviewStatus: data.reviewStatus,
    };
  }

  const headerInfo = lastHeaderRef.current;
  const showContent = !isLoading && !isError && !!data;
  const showInitialLoading = isLoading && !headerInfo;
  const showTransitionLoading = (isLoading || isNavigating) && !!headerInfo;

  const handleQueueNavigateWithLoading = async (item: QueueReportItem) => {
    setIsNavigating(true);
    await handleQueueNavigate(item);
  };

  const handleContinueToNextWithLoading = async () => {
    setIsNavigating(true);
    await handleContinueToNext();
  };

  if (showInitialLoading) {
    return (
      <div className="container mx-auto py-6 px-4 space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (isError && !isNavigating && !headerInfo) {
    return (
      <div className="container mx-auto py-6 px-4">
        <div className="flex items-center gap-2 p-4 text-destructive bg-destructive/10 rounded-md" data-testid="text-error">
          <AlertCircle className="h-5 w-5" />
          <span>Report not found or no longer needs review.</span>
        </div>
        <Button variant="outline" className="mt-4" asChild>
          <Link href="/reports/review">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Queue
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Queue navigation bar — above header */}
      <div className="sticky top-0 z-20 border-b bg-muted/30 px-4 py-2" data-testid="review-nav-bar">
        <div className="container mx-auto flex items-center justify-between gap-4">
          <Button variant="outline" size="icon" asChild className="sm:hidden" data-testid="button-back-to-queue">
            <Link href="/reports/review">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild className="hidden sm:flex" data-testid="button-back-to-queue-desktop">
            <Link href="/reports/review">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Queue
            </Link>
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!prevQueueItem || isNavigating}
              onClick={() => prevQueueItem && handleQueueNavigateWithLoading(prevQueueItem)}
              data-testid="button-queue-prev"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Prev</span>
            </Button>
            <span className="text-sm text-muted-foreground whitespace-nowrap" data-testid="text-queue-position">
              {acceptedState
                ? acceptedState.positionLabel
                : sessionTotalRef.current > 0 && sessionPosition > 0
                  ? `Reviewing report ${sessionPosition} of ${sessionTotalRef.current}`
                  : currentQueueIndex >= 0
                    ? `Reviewing report ${currentQueueIndex + 1} of ${sortedQueue.length}`
                    : pendingCount > 0
                      ? `${pendingCount} pending`
                      : "Queue empty"}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!nextQueueItem || isNavigating}
              onClick={() => nextQueueItem && handleQueueNavigateWithLoading(nextQueueItem)}
              data-testid="button-queue-next"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="w-[72px]" />
        </div>
      </div>

      {/* Sticky header — title vertically centered with actions */}
      <div className="sticky top-[41px] z-10 bg-background border-b px-4 py-3">
        <div className="container mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
            <h1 className="text-2xl font-semibold tracking-tight leading-none">
              <span className="flex items-baseline gap-2 flex-wrap">
                <span>Review: {reportTypeLabel}</span>
                {locationLabel && (
                  <Badge variant="secondary" className="text-sm font-normal align-middle">{locationLabel}</Badge>
                )}
                {reportDateFormatted && (
                  <span className="text-base font-normal text-muted-foreground">({reportDateFormatted})</span>
                )}
              </span>
            </h1>
            {reparseSentinel && (
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="text-reparsed-label">
                Reparsed {formatDistanceToNow(new Date(reparseSentinel.editedAt), { addSuffix: true })}
              </p>
            )}
          </div>
              {!acceptedState && !queueEmptyState && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={!data}
                      data-testid="button-secondary-actions"
                    >
                      <MoreVertical className="h-4 w-4" />
                      <span className="sr-only">More actions</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => {
                        const href = isAllPets
                          ? `/api/reports/${data?.sourceReportId || headerInfo?.sourceReportId}/file`
                          : `/api/behavior/reports/${data?.sourceReportId || headerInfo?.sourceReportId}/file`;
                        window.open(href, "_blank", "noopener,noreferrer");
                      }}
                      disabled={!data}
                      data-testid="link-view-pdf"
                    >
                      <FileText className="h-4 w-4" />
                      View PDF
                      <ExternalLink className="h-3.5 w-3.5 ml-auto" />
                    </DropdownMenuItem>
                    {(data?.editHistory?.length || headerInfo?.editHistoryLength || 0) > 0 && (
                      <DropdownMenuItem
                        onClick={() => setShowHistory(true)}
                        data-testid="button-show-history"
                      >
                        <History className="h-4 w-4" />
                        {data?.editHistory?.length || headerInfo?.editHistoryLength || 0} Edit{(data?.editHistory?.length || headerInfo?.editHistoryLength || 0) !== 1 ? "s" : ""}
                      </DropdownMenuItem>
                    )}
                    {isSuperAdmin && (
                      <DropdownMenuItem
                        onClick={() => setShowReparseConfirm(true)}
                        disabled={!data || (data?.reviewStatus || headerInfo?.reviewStatus) !== "pending" || reparseReportMutation.isPending || showTransitionLoading}
                        data-testid="button-reparse-report"
                      >
                        {reparseReportMutation.isPending
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <RotateCcw className="h-4 w-4" />}
                        Reparse from PDF
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                {acceptanceDisabled ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0} className="inline-flex" data-testid="button-accept-report-blocked">
                        <Button
                          disabled
                          data-testid="button-accept-report"
                          className="pointer-events-none"
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          <span className="sm:hidden">Accept</span>
                          <span className="hidden sm:inline">Accept Report</span>
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      Acceptance is currently disabled. Contact a super admin for details.
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Button
                    onClick={() => acceptMutation.mutate()}
                    disabled={!data || (data?.reviewStatus || headerInfo?.reviewStatus) !== "pending" || acceptMutation.isPending || showTransitionLoading}
                    data-testid="button-accept-report"
                  >
                    {acceptMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                    )}
                    <span className="sm:hidden">Accept</span>
                    <span className="hidden sm:inline">Accept Report</span>
                  </Button>
                )}
              </div>
              )}
          </div>
        </div>
      </div>

      {/* Loading state between reports */}
      {showTransitionLoading && (
        <div className="flex-1 flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Loading report...</span>
          </div>
        </div>
      )}

      {/* Queue empty state (concurrent reviewers cleared the queue) */}
      {showTransitionLoading ? null : queueEmptyState && !acceptedState ? (
        <div className="flex-1 overflow-auto px-4 py-6">
          <div className="container mx-auto max-w-lg">
            <Card>
              <CardContent className="py-12 flex flex-col items-center text-center gap-4">
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="space-y-1">
                  <h2 className="text-xl font-semibold" data-testid="text-queue-empty-title">No Pending Reports</h2>
                  <p className="text-muted-foreground" data-testid="text-queue-empty-detail">
                    All reports in the queue have been reviewed.
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <Button variant="outline" asChild data-testid="button-back-queue">
                    <Link href="/reports/review">Back to Queue</Link>
                  </Button>
                  <Button variant="outline" asChild data-testid="button-back-dashboard-empty">
                    <Link href="/">Dashboard</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : acceptedState ? (
        <div className="flex-1 overflow-auto px-4 py-6">
          <div className="container mx-auto max-w-lg">
            <Card>
              <CardContent className="py-12 flex flex-col items-center text-center gap-4">
                <div className="h-16 w-16 rounded-full bg-success/10 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-success" />
                </div>
                <div className="space-y-1">
                  <h2 className="text-xl font-semibold" data-testid="text-accept-title">Report Accepted</h2>
                  <p className="text-muted-foreground" data-testid="text-accept-detail">
                    {acceptedState.reportType === "all_pets" ? "All Pets" : "Interaction"} report
                    {acceptedState.location ? ` for ${acceptedState.location}` : ""}
                    {acceptedState.reportDate ? ` (${acceptedState.reportDate})` : ""}
                    {" "}has been promoted to canonical data.
                  </p>
                </div>
                <Separator className="my-2" />
                {postAcceptNextItem ? (
                  <div className="space-y-3 w-full">
                    <p className="text-sm text-muted-foreground">
                      Next up: <span className="font-medium text-foreground">{postAcceptNextItem.sourceReportType === "all_pets" ? "All Pets" : "Interaction"}</span>
                      {postAcceptNextItem.parsedData?.location ? ` — ${postAcceptNextItem.parsedData.location}` : ""}
                      {postAcceptNextItem.parsedData?.reportDate ? ` (${postAcceptNextItem.parsedData.reportDate})` : ""}
                      {postAcceptNextItem.reviewReason ? (
                        <span className="block text-xs mt-0.5">{postAcceptNextItem.reviewReason.length > 80 ? postAcceptNextItem.reviewReason.slice(0, 77) + "..." : postAcceptNextItem.reviewReason}</span>
                      ) : null}
                    </p>
                    <Button
                      onClick={handleContinueToNextWithLoading}
                      data-testid="button-continue-next"
                    >
                      Continue to Next Report
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 w-full">
                    <p className="text-sm font-medium text-success" data-testid="text-queue-complete">
                      All reports have been reviewed
                    </p>
                    <Button variant="outline" asChild data-testid="button-back-dashboard">
                      <Link href="/">Back to Dashboard</Link>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      ) : !data ? (
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Loading report...</span>
        </div>
      </div>
      ) : (
      <div className="flex-1 overflow-auto px-4 py-6">
        <div className="container mx-auto space-y-6">
          <Card>
            <CardHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="text-lg">Report Details</CardTitle>
              <CardDescription>
                {(() => {
                  const parsed = parseEmailIngestHoldReason(data.reviewReason);
                  if (parsed.isEmailIngestHold) {
                    return (
                      <span className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="shrink-0 text-xs bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800" data-testid="badge-email-hold">
                          Email review required
                        </Badge>
                        {parsed.description && (
                          <span className="text-muted-foreground">{parsed.description}</span>
                        )}
                      </span>
                    );
                  }
                  return data.reviewReason || "Pending review";
                })()}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {/* Gate I9.2 Phase 2: Provenance indicator */}
              {data.sourceEmailId && (
                <Badge variant="outline" className="gap-1 text-xs" data-testid="badge-email-ingest">
                  <Mail className="h-3 w-3" />
                  Email ingest
                </Badge>
              )}
              <Badge variant="secondary" className="gap-1">
                {isAllPets ? <FileText className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />}
                {data.reviewStatus}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Location</p>
              {editingLocation !== null ? (
                <div className="flex items-center gap-1 mt-1">
                  <Select
                    value={editingLocation}
                    onValueChange={setEditingLocation}
                  >
                    <SelectTrigger className="h-8" data-testid="select-edit-location">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {activeLocations.map((loc) => (
                        <SelectItem key={loc.code} value={loc.code}>
                          {loc.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => handleLocationSave(editingLocation)}
                    disabled={editMutation.isPending}
                    data-testid="button-save-location"
                  >
                    {editMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => setEditingLocation(null)}
                    data-testid="button-cancel-location"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <p className="font-medium">{data.parsedData?.location || "—"}</p>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    onClick={() => setEditingLocation(data.parsedData?.location || "")}
                    data-testid="button-edit-location"
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Report Date</p>
              <p className="font-medium">{data.parsedData?.reportDate || "—"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Items</p>
              <p className="font-medium">{items.length}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Created</p>
              <p className="font-medium">{format(new Date(data.createdAt), "MM/dd/yyyy HH:mm")}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* G3.4: Blocking Issues (red, prevents acceptance) */}
      {isAllPets && (() => {
        const animalsData = data.parsedData?.animals || [];
        const locationCode = (data.parsedData?.location || "").toUpperCase();
        const isLocationValid = activeLocations.some(l => l.code.toUpperCase() === locationCode);
        
        const animalsWithMissingId = animalsData.filter((a) => 
          !a.animalId || a.animalId.trim() === ""
        );
        const animalsWithBadSpecies = animalsData.filter((a) => 
          !a.species || a.species.toUpperCase() === "UNKNOWN" || a.species.toUpperCase() === "OTHER"
        );
        const animalsWithBadStatus = animalsData.filter((a: any) => 
          !a.statusCode || a.statusCode?.toUpperCase() === "UNKNOWN"
        );
        
        const hasBlockingIssues = !isLocationValid || animalsWithMissingId.length > 0 || animalsWithBadSpecies.length > 0 || animalsWithBadStatus.length > 0;
        
        if (!hasBlockingIssues) return null;
        
        return (
          <Card className="border-l-4 border-l-destructive">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-destructive" />
                <CardTitle className="text-lg text-destructive">
                  Blocking Issues
                </CardTitle>
              </div>
              <CardDescription>
                The following issues must be resolved before this report can be accepted.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {!isLocationValid && (
                <div className="flex items-start gap-2" data-testid="blocking-location">
                  <Badge variant="destructive" className="shrink-0">Location</Badge>
                  <span className="text-sm">
                    "{data.parsedData?.location || "—"}" is not a recognized active location. 
                    Click the edit icon above to correct it.
                  </span>
                </div>
              )}
              {animalsWithMissingId.length > 0 && (
                <div className="flex items-start gap-2" data-testid="blocking-animalid">
                  <Badge variant="destructive" className="shrink-0">Animal ID</Badge>
                  <span className="text-sm">
                    {animalsWithMissingId.length} animal(s) are missing an Animal ID. 
                    Edit the Animal ID in the table below.
                  </span>
                </div>
              )}
              {animalsWithBadSpecies.length > 0 && (
                <div className="flex items-start gap-2" data-testid="blocking-species">
                  <Badge variant="destructive" className="shrink-0">Species</Badge>
                  <span className="text-sm">
                    {animalsWithBadSpecies.length} animal(s) have Unknown or Other species: {" "}
                    {animalsWithBadSpecies.slice(0, 5).map((a) => a.animalId).join(", ")}
                    {animalsWithBadSpecies.length > 5 && ` and ${animalsWithBadSpecies.length - 5} more`}.
                    Edit the species in the table below.
                  </span>
                </div>
              )}
              {animalsWithBadStatus.length > 0 && (
                <div className="flex items-start gap-2" data-testid="blocking-status">
                  <Badge variant="destructive" className="shrink-0">Status</Badge>
                  <span className="text-sm">
                    {animalsWithBadStatus.length} animal(s) have Unknown status: {" "}
                    {animalsWithBadStatus.slice(0, 5).map((a: any) => a.animalId).join(", ")}
                    {animalsWithBadStatus.length > 5 && ` and ${animalsWithBadStatus.length - 5} more`}.
                    Edit the status in the table below.
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Kennel warnings (informational, non-blocking) */}
      {isAllPets && data.parseDiagnostics?.unknownZones && data.parseDiagnostics.unknownZones.length > 0 && (
        <Card className="border-l-4 border-l-warning">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-warning" />
                <CardTitle className="text-lg text-warning">
                  Unrecognized Kennels
                </CardTitle>
              </div>
              {isAdmin && (
                <a
                  href="/admin/settings#kennels"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                  data-testid="link-manage-kennels"
                >
                  Manage Kennels
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
            <CardDescription>
              The following kennels were found in the report but are not configured in the system. 
              This is informational only and does not block acceptance.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2" data-testid="kennel-warnings">
              {data.parseDiagnostics.unknownZones.map((zone, idx) => (
                <Badge 
                  key={idx} 
                  variant="outline" 
                  className="text-warning border-warning/50"
                  data-testid={`badge-unknown-kennel-${idx}`}
                >
                  {zone}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Species confirmation warning (All Pets reports only, non-blocking) */}
      {isAllPets && (() => {
        const unknownSpeciesIds = getUnknownSpeciesAnimalIds(data);
        if (unknownSpeciesIds.length === 0) return null;
        
        return (
          <Card className="border-l-4 border-l-warning" data-testid="card-species-confirmation">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-warning" />
                <CardTitle className="text-lg text-warning">
                  Species Confirmation Needed
                </CardTitle>
              </div>
              <CardDescription>
                These animals had Unknown or Other species when the PDF was parsed. Their species may have been 
                automatically filled in from existing records. Please confirm the values are correct or edit if needed.
                This is informational only and does not block acceptance.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2" data-testid="species-confirmation-warnings">
                {unknownSpeciesIds.map((animalId, idx) => (
                  <Badge 
                    key={idx} 
                    variant="outline" 
                    className="text-warning border-warning/50 cursor-pointer hover:bg-warning/10 transition-colors"
                    onClick={() => scrollToAnimalRow(animalId)}
                    data-testid={`badge-unknown-species-${idx}`}
                  >
                    {animalId}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Unknown animal IDs warning (interaction reports only, non-blocking) */}
      {!isAllPets && data.parseDiagnostics?.unknownAnimalIds && data.parseDiagnostics.unknownAnimalIds.length > 0 && (
        <Card className="border-l-4 border-l-warning">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-warning" />
              <CardTitle className="text-lg text-warning">
                New Animal IDs
              </CardTitle>
            </div>
            <CardDescription>
              These animals haven't been seen in an All Pets report yet. If you have an All Pets report 
              for this date, consider uploading it first. Otherwise, you can proceed — the animals will 
              be linked when their All Pets report is uploaded later.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2" data-testid="unknown-animal-warnings">
              {data.parseDiagnostics.unknownAnimalIds.map((animalId, idx) => (
                <Badge 
                  key={idx} 
                  variant="outline" 
                  className="text-warning border-warning/50"
                  data-testid={`badge-unknown-animal-${idx}`}
                >
                  {animalId}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Items needing review section (at top) */}
      {isAllPets ? (() => {
        const allAnimals = (items as PendingReportData["parsedData"]["animals"]) || [];
        const unknownSpeciesIds = getUnknownSpeciesAnimalIds(data);
        const animalsNeedingReview = allAnimals.filter((a) => 
          !a.animalId || a.animalId.trim() === "" ||
          !a.species || a.species.toUpperCase() === "UNKNOWN" || a.species.toUpperCase() === "OTHER" ||
          !a.statusCode || a.statusCode?.toUpperCase() === "UNKNOWN" ||
          unknownSpeciesIds.includes(a.animalId)
        );
        const animalsOk = allAnimals.filter((a) => 
          a.animalId && a.animalId.trim() !== "" &&
          a.species && a.species.toUpperCase() !== "UNKNOWN" && a.species.toUpperCase() !== "OTHER" &&
          a.statusCode && a.statusCode?.toUpperCase() !== "UNKNOWN" &&
          !unknownSpeciesIds.includes(a.animalId)
        );

        return (
          <>
            {animalsNeedingReview.length > 0 && (
              <Card className="border-amber-500/30">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                    Needs Review ({animalsNeedingReview.length})
                  </CardTitle>
                  <CardDescription>
                    These animals have missing data or need species confirmation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Animal ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Kennel</TableHead>
                        <TableHead>Species</TableHead>
                        <TableHead>Breed</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {animalsNeedingReview.map((animal) => {
                        const idx = allAnimals.indexOf(animal);
                        const hasMissingId = !animal.animalId || animal.animalId.trim() === "";
                        const hasBadSpecies = !animal.species || animal.species.toUpperCase() === "UNKNOWN" || animal.species.toUpperCase() === "OTHER";
                        const hasBadStatus = !animal.statusCode || animal.statusCode?.toUpperCase() === "UNKNOWN";
                        const needsSpeciesConfirmation = unknownSpeciesIds.includes(animal.animalId);
                        const isActivelyHighlighted = highlightedAnimalId === animal.animalId;
                        return (
                          <TableRow 
                            key={idx} 
                            data-testid={`row-animal-${idx}`}
                            data-animal-id={animal.animalId}
                            className=""
                          >
                            <TableCell className={cn(hasMissingId && "bg-warning/10 dark:bg-warning/15")}>
                              <EditableCell
                                value={animal.animalId}
                                isEditing={editingCell?.itemIndex === idx && editingCell?.field === "animalId"}
                                editValue={editingCell?.value || ""}
                                onEdit={() => handleCellEdit(idx, "animalId", animal.animalId)}
                                onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                                onSave={handleCellSave}
                                onCancel={() => setEditingCell(null)}
                                isPending={editMutation.isPending}
                                testIdPrefix={`animal-${idx}-animalId`}
                                hasError={hasMissingId}
                                errorVariant="warning"
                              />
                            </TableCell>
                            <TableCell>{animal.name}</TableCell>
                            <TableCell>
                              <EditableSelectCell
                                value={animal.zone || ""}
                                isEditing={editingCell?.itemIndex === idx && editingCell?.field === "zone"}
                                editValue={editingCell?.value || ""}
                                options={zoneOptions}
                                onEdit={() => handleCellEdit(idx, "zone", animal.zone || "")}
                                onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                                onSave={handleCellSave}
                                onCancel={() => setEditingCell(null)}
                                isPending={editMutation.isPending}
                                testIdPrefix={`animal-${idx}-zone`}
                                placeholder="Select zone"
                              />
                            </TableCell>
                            <TableCell className={cn((hasBadSpecies || needsSpeciesConfirmation) && "bg-warning/10 dark:bg-warning/15")}>
                              <EditableSelectCell
                                value={animal.species || ""}
                                isEditing={editingCell?.itemIndex === idx && editingCell?.field === "species"}
                                editValue={editingCell?.value || ""}
                                options={SPECIES_OPTIONS}
                                onEdit={() => handleCellEdit(idx, "species", animal.species || "")}
                                onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                                onSave={handleCellSave}
                                onCancel={() => setEditingCell(null)}
                                isPending={editMutation.isPending}
                                testIdPrefix={`animal-${idx}-species`}
                                placeholder="Select species"
                                hasError={hasBadSpecies || needsSpeciesConfirmation}
                                errorVariant="warning"
                              />
                            </TableCell>
                            <TableCell data-testid={`animal-${idx}-breed`}>
                              <EditableCell
                                value={(animal as any).breed || ""}
                                isEditing={editingCell?.itemIndex === idx && editingCell?.field === "breed"}
                                editValue={editingCell?.value || ""}
                                onEdit={() => handleCellEdit(idx, "breed", (animal as any).breed || "")}
                                onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                                onSave={handleCellSave}
                                onCancel={() => setEditingCell(null)}
                                isPending={editMutation.isPending}
                                testIdPrefix={`animal-${idx}-breed`}
                              />
                            </TableCell>
                            <TableCell className={cn(hasBadStatus && "bg-warning/10 dark:bg-warning/15")}>
                              <EditableSelectCell
                                value={animal.statusCode || ""}
                                isEditing={editingCell?.itemIndex === idx && editingCell?.field === "statusCode"}
                                editValue={editingCell?.value || ""}
                                options={STATUS_CODE_OPTIONS}
                                onEdit={() => handleCellEdit(idx, "statusCode", animal.statusCode || "")}
                                onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                                onSave={handleCellSave}
                                onCancel={() => setEditingCell(null)}
                                isPending={editMutation.isPending}
                                testIdPrefix={`animal-${idx}-statusCode`}
                                placeholder="Select status"
                                hasError={hasBadStatus}
                                errorVariant="warning"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {animalsNeedingReview.length > 0 ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      Parsed Animals ({animalsOk.length})
                    </>
                  ) : (
                    <>Parsed Animals ({allAnimals.length})</>
                  )}
                </CardTitle>
                <CardDescription>
                  {animalsNeedingReview.length > 0 
                    ? "These animals have valid data" 
                    : "Click the pencil icon to edit a field"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Animal ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Kennel</TableHead>
                      <TableHead>Species</TableHead>
                      <TableHead>Breed</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(animalsNeedingReview.length > 0 ? animalsOk : allAnimals).map((animal) => {
                      const idx = allAnimals.indexOf(animal);
                      const unknownSpeciesIds = getUnknownSpeciesAnimalIds(data);
                      const isHighlightedFromDiagnostics = unknownSpeciesIds.includes(animal.animalId);
                      const isActivelyHighlighted = highlightedAnimalId === animal.animalId;
                      return (
                        <TableRow 
                          key={idx} 
                          data-testid={`row-animal-${idx}`}
                          data-animal-id={animal.animalId}
                          className={cn(
                            isHighlightedFromDiagnostics && "bg-amber-50 dark:bg-transparent",
                            isActivelyHighlighted && "bg-amber-200 dark:bg-transparent transition-colors"
                          )}
                        >
                          <TableCell>
                            <EditableCell
                              value={animal.animalId}
                              isEditing={editingCell?.itemIndex === idx && editingCell?.field === "animalId"}
                              editValue={editingCell?.value || ""}
                              onEdit={() => handleCellEdit(idx, "animalId", animal.animalId)}
                              onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                              onSave={handleCellSave}
                              onCancel={() => setEditingCell(null)}
                              isPending={editMutation.isPending}
                              testIdPrefix={`animal-${idx}-animalId`}
                            />
                          </TableCell>
                          <TableCell>{animal.name}</TableCell>
                          <TableCell>
                            <EditableSelectCell
                              value={animal.zone || ""}
                              isEditing={editingCell?.itemIndex === idx && editingCell?.field === "zone"}
                              editValue={editingCell?.value || ""}
                              options={zoneOptions}
                              onEdit={() => handleCellEdit(idx, "zone", animal.zone || "")}
                              onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                              onSave={handleCellSave}
                              onCancel={() => setEditingCell(null)}
                              isPending={editMutation.isPending}
                              testIdPrefix={`animal-${idx}-zone`}
                              placeholder="Select zone"
                            />
                          </TableCell>
                          <TableCell>
                            <EditableSelectCell
                              value={animal.species || ""}
                              isEditing={editingCell?.itemIndex === idx && editingCell?.field === "species"}
                              editValue={editingCell?.value || ""}
                              options={SPECIES_OPTIONS}
                              onEdit={() => handleCellEdit(idx, "species", animal.species || "")}
                              onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                              onSave={handleCellSave}
                              onCancel={() => setEditingCell(null)}
                              isPending={editMutation.isPending}
                              testIdPrefix={`animal-${idx}-species`}
                              placeholder="Select species"
                            />
                          </TableCell>
                          <TableCell data-testid={`animal-${idx}-breed`}>
                            <span className={cn(!(animal as any).breed && "text-muted-foreground")}>
                              {(animal as any).breed || "—"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <EditableSelectCell
                              value={animal.statusCode || ""}
                              isEditing={editingCell?.itemIndex === idx && editingCell?.field === "statusCode"}
                              editValue={editingCell?.value || ""}
                              options={STATUS_CODE_OPTIONS}
                              onEdit={() => handleCellEdit(idx, "statusCode", animal.statusCode || "")}
                              onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                              onSave={handleCellSave}
                              onCancel={() => setEditingCell(null)}
                              isPending={editMutation.isPending}
                              testIdPrefix={`animal-${idx}-statusCode`}
                              placeholder="Select status"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        );
      })() : (() => {
        const allInteractions = (items as PendingReportData["parsedData"]["interactions"]) || [];
        const unknownAnimalIds = interactionUnknownAnimalIds;
        const interactionsNeedingReview = allInteractions.filter((i) => 
          interactionNeedsReview(i, unknownAnimalIds)
        );
        const interactionsOk = allInteractions.filter((i) => 
          !interactionNeedsReview(i, unknownAnimalIds)
        );

        return (
          <>
            {interactionsNeedingReview.length > 0 && (
              <Card className="border-amber-500/30">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                    Needs Review ({interactionsNeedingReview.length})
                  </CardTitle>
                  <CardDescription>
                    These interactions have new animal IDs, missing scores, or missing authors
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Animal ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Species</TableHead>
                        <TableHead>Score</TableHead>
                        <TableHead>Author</TableHead>
                        <TableHead>Note</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {interactionsNeedingReview.map((interaction) => {
                        const idx = allInteractions.indexOf(interaction);
                        const isUnknownAnimal = unknownAnimalIds.includes(interaction.animalId);
                        const hasMissingScore = !interaction.scoreCode;
                        const hasMissingAuthor = !interaction.author;
                        return (
                          <TableRow key={idx} data-testid={`row-interaction-${idx}`}>
                            <TableCell className={cn(isUnknownAnimal && "bg-warning/10 dark:bg-warning/15")}>
                              <span 
                                className={cn(
                                  "text-sm font-mono",
                                  isUnknownAnimal && "text-warning-foreground"
                                )}
                                data-testid={`interaction-${idx}-animalId`}
                              >
                                {interaction.animalId}
                              </span>
                            </TableCell>
                            <TableCell data-testid={`interaction-${idx}-name`}>
                              {interaction.animalDbId ? (
                                <Link 
                                  href={`/animals/${interaction.animalDbId}`} 
                                  className="text-primary hover:underline"
                                  data-testid={`link-animal-${idx}`}
                                >
                                  {interaction.animalName || "—"}
                                </Link>
                              ) : (
                                <span className="text-muted-foreground">{interaction.animalName || "—"}</span>
                              )}
                            </TableCell>
                            <TableCell data-testid={`interaction-${idx}-species`}>
                              <span className={cn(!interaction.species && "text-muted-foreground")}>
                                {interaction.species || "—"}
                              </span>
                            </TableCell>
                            <TableCell className={cn(hasMissingScore && "bg-warning/10 dark:bg-warning/15")}>
                              <EditableSelectCell
                                value={interaction.scoreCode || ""}
                                isEditing={editingCell?.itemIndex === idx && editingCell?.field === "scoreCode"}
                                editValue={editingCell?.value || ""}
                                options={SCORE_CODE_OPTIONS}
                                onEdit={() => handleCellEdit(idx, "scoreCode", interaction.scoreCode || "")}
                                onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                                onSave={handleCellSave}
                                onCancel={() => setEditingCell(null)}
                                isPending={editMutation.isPending}
                                testIdPrefix={`interaction-${idx}-scoreCode`}
                                placeholder="Select score"
                                hasError={hasMissingScore}
                                errorVariant="warning"
                              />
                            </TableCell>
                            <TableCell className={cn(hasMissingAuthor && "bg-warning/10 dark:bg-warning/15")}>
                              <EditableCell
                                value={interaction.author || ""}
                                isEditing={editingCell?.itemIndex === idx && editingCell?.field === "author"}
                                editValue={editingCell?.value || ""}
                                onEdit={() => handleCellEdit(idx, "author", interaction.author || "")}
                                onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                                onSave={handleCellSave}
                                onCancel={() => setEditingCell(null)}
                                isPending={editMutation.isPending}
                                testIdPrefix={`interaction-${idx}-author`}
                              />
                            </TableCell>
                            <TableCell className="max-w-[200px]">
                              <div className="flex items-center gap-1">
                                <div className="flex-1 min-w-0">
                                  <EditableCell
                                    value={interaction.rawNote || ""}
                                    isEditing={editingCell?.itemIndex === idx && editingCell?.field === "rawNote"}
                                    editValue={editingCell?.value || ""}
                                    onEdit={() => handleCellEdit(idx, "rawNote", interaction.rawNote || "")}
                                    onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                                    onSave={handleCellSave}
                                    onCancel={() => setEditingCell(null)}
                                    isPending={editMutation.isPending}
                                    testIdPrefix={`interaction-${idx}-rawNote`}
                                  />
                                </div>
                                {!(editingCell?.itemIndex === idx && editingCell?.field === "rawNote") && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="flex-shrink-0 px-2"
                                    onClick={() => handleOpenInteractionDrawer(interaction, idx)}
                                    data-testid={`button-view-interaction-${idx}`}
                                  >
                                    <Eye className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {interactionsNeedingReview.length > 0 ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      Parsed Interactions ({interactionsOk.length})
                    </>
                  ) : (
                    <>Parsed Interactions ({allInteractions.length})</>
                  )}
                </CardTitle>
                <CardDescription>
                  {interactionsNeedingReview.length > 0 
                    ? "These interactions have valid data" 
                    : "Click the pencil icon to edit a field"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Animal ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Species</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Author</TableHead>
                      <TableHead>Note</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(interactionsNeedingReview.length > 0 ? interactionsOk : allInteractions).map((interaction) => {
                      const idx = allInteractions.indexOf(interaction);
                      return (
                        <TableRow key={idx} data-testid={`row-interaction-${idx}`}>
                          <TableCell>
                            <span 
                              className="text-sm font-mono"
                              data-testid={`interaction-${idx}-animalId`}
                            >
                              {interaction.animalId}
                            </span>
                          </TableCell>
                          <TableCell data-testid={`interaction-${idx}-name`}>
                            {interaction.animalDbId ? (
                              <Link 
                                href={`/animals/${interaction.animalDbId}`} 
                                className="text-primary hover:underline"
                                data-testid={`link-animal-${idx}`}
                              >
                                {interaction.animalName || "—"}
                              </Link>
                            ) : (
                              <span className="text-muted-foreground">{interaction.animalName || "—"}</span>
                            )}
                          </TableCell>
                          <TableCell data-testid={`interaction-${idx}-species`}>
                            <span className={cn(!interaction.species && "text-muted-foreground")}>
                              {interaction.species || "—"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <EditableSelectCell
                              value={interaction.scoreCode || ""}
                              isEditing={editingCell?.itemIndex === idx && editingCell?.field === "scoreCode"}
                              editValue={editingCell?.value || ""}
                              options={SCORE_CODE_OPTIONS}
                              onEdit={() => handleCellEdit(idx, "scoreCode", interaction.scoreCode || "")}
                              onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                              onSave={handleCellSave}
                              onCancel={() => setEditingCell(null)}
                              isPending={editMutation.isPending}
                              testIdPrefix={`interaction-${idx}-scoreCode`}
                              placeholder="Select score"
                            />
                          </TableCell>
                          <TableCell>
                            <EditableCell
                              value={interaction.author || ""}
                              isEditing={editingCell?.itemIndex === idx && editingCell?.field === "author"}
                              editValue={editingCell?.value || ""}
                              onEdit={() => handleCellEdit(idx, "author", interaction.author || "")}
                              onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                              onSave={handleCellSave}
                              onCancel={() => setEditingCell(null)}
                              isPending={editMutation.isPending}
                              testIdPrefix={`interaction-${idx}-author`}
                            />
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            <div className="flex items-center gap-1">
                              <div className="flex-1 min-w-0">
                                <EditableCell
                                  value={interaction.rawNote || ""}
                                  isEditing={editingCell?.itemIndex === idx && editingCell?.field === "rawNote"}
                                  editValue={editingCell?.value || ""}
                                  onEdit={() => handleCellEdit(idx, "rawNote", interaction.rawNote || "")}
                                  onChange={(v) => setEditingCell(prev => prev ? { ...prev, value: v } : null)}
                                  onSave={handleCellSave}
                                  onCancel={() => setEditingCell(null)}
                                  isPending={editMutation.isPending}
                                  testIdPrefix={`interaction-${idx}-rawNote`}
                                />
                              </div>
                              {!(editingCell?.itemIndex === idx && editingCell?.field === "rawNote") && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="flex-shrink-0 px-2"
                                  onClick={() => handleOpenInteractionDrawer(interaction, idx)}
                                  data-testid={`button-view-interaction-ok-${idx}`}
                                >
                                  <Eye className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        );
      })()}
        </div>
      </div>
      )}

      <Dialog open={showHistory && !!data} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit History</DialogTitle>
            <DialogDescription>
              {data?.editHistory?.length || 0} edit{data?.editHistory?.length !== 1 ? "s" : ""} made to this report
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Field</TableHead>
                  <TableHead>Old Value</TableHead>
                  <TableHead>New Value</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.editHistory?.map((entry, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-mono text-sm">
                      {entry.field === "__system_reparse__" ? (
                        <span className="text-muted-foreground italic">System reparse</span>
                      ) : (
                        <>
                          {entry.field}
                          {entry.itemIndex !== undefined && `[${entry.itemIndex}]`}
                        </>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-[150px] truncate">
                      {entry.field === "__system_reparse__" ? "—" : (String(entry.oldValue) || "—")}
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate">
                      {entry.field === "__system_reparse__" ? "—" : String(entry.newValue)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(entry.editedAt), "MM/dd HH:mm")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* P28: Reparse from PDF confirmation */}
      <AlertDialog open={showReparseConfirm} onOpenChange={setShowReparseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reparse from PDF?</AlertDialogTitle>
            <AlertDialogDescription>
              This will re-fetch the original PDF and run the parser again. Any manual edits on this
              report will be overwritten. If the reparse finds no issues, the report will be
              auto-accepted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-reparse-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowReparseConfirm(false);
                reparseReportMutation.mutate();
              }}
              data-testid="button-reparse-confirm"
            >
              Reparse
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* G3.4: Cancel confirmation dialog for unsaved edits */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved edits that will be lost. Are you sure you want to leave this page?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-stay">Stay</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setEditingCell(null);
                setEditingLocation(null);
                setShowCancelConfirm(false);
                setLocation("/reports/review");
              }}
              data-testid="button-leave"
            >
              Leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pending Interaction Detail Sheet */}
      <Sheet 
        open={selectedPendingInteraction !== null} 
        onOpenChange={(open) => !open && handleDrawerClose()}
      >
        <SheetContent 
          className="!w-full max-w-full sm:!w-[440px] sm:max-w-[480px] p-0 flex flex-col"
          data-testid="pending-interaction-drawer"
        >
          <div className="sticky top-0 z-50 bg-background border-b border-border px-6 py-3">
            <SheetHeader className="flex flex-col gap-1.5">
              {/* Row 1: title + close */}
              <div className="flex items-center justify-between gap-2">
                <SheetTitle className="flex items-center gap-2 min-w-0">
                  <FileText className="h-5 w-5 flex-shrink-0" />
                  <span>Interaction Details</span>
                </SheetTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  className="flex-shrink-0"
                  onClick={handleDrawerClose}
                  data-testid="pending-interaction-drawer-close"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </div>
              {/* Row 2: review navigation */}
              {totalInteractionCount > 1 && (
                <div className="flex items-center justify-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleDrawerPrevious}
                    disabled={!canGoPrevious}
                    data-testid="pending-interaction-drawer-previous"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="sr-only">Previous needing review</span>
                  </Button>
                  <span className="text-sm text-muted-foreground min-w-[60px] text-center" data-testid="pending-interaction-drawer-position">
                    {reviewCount === 0
                      ? "0 needing review"
                      : currentReviewPosition !== null
                        ? `${currentReviewPosition} of ${reviewCount} to review`
                        : `${reviewCount} needing review`}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleDrawerNext}
                    disabled={!canGoNext}
                    data-testid="pending-interaction-drawer-next"
                  >
                    <ChevronRight className="h-4 w-4" />
                    <span className="sr-only">Next needing review</span>
                  </Button>
                </div>
              )}
            </SheetHeader>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-6 space-y-6">
              {selectedPendingInteraction && (
                <>
                  {/* Preview footnote */}
                  <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                    <Info className="h-3.5 w-3.5 flex-shrink-0" />
                    This record is not visible in BETI until report is accepted.
                  </p>

                  {/* Animal Details Section */}
                  <div data-testid="pending-interaction-animal-summary">
                    <h3 className="font-semibold mb-2">Animal Details</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <AnimalAvatar animalId={selectedPendingInteraction.animalId || ""} photoUrl={null} name={selectedPendingInteraction.animalName || selectedPendingInteraction.animalId || "?"} species={selectedPendingInteraction.species || "DOG"} className="h-8 w-8" />
                        <div className="min-w-0">
                          <div className="font-medium truncate">
                            {selectedPendingInteraction.animalName || selectedPendingInteraction.animalId}
                          </div>
                          <div className="text-muted-foreground text-xs">{selectedPendingInteraction.animalId}</div>
                        </div>
                        {selectedPendingInteraction.animalDbId && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="gap-1.5 ml-auto flex-shrink-0"
                            onClick={() => {
                              window.open(`/animals/${selectedPendingInteraction.animalId}`, '_blank');
                            }}
                            data-testid="pending-interaction-view-animal"
                          >
                            View
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pl-10">
                        {selectedPendingInteraction.species && (
                          <span>{selectedPendingInteraction.species}</span>
                        )}
                        {data?.parsedData?.location && (
                          <span>{locationLabel}</span>
                        )}
                        {drawerAnimalDbId && drawerAnimalData?.currentStatus?.kennelStatusNorm && (
                          <span className={cn("inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium", getKennelStatusBadgeClass(drawerAnimalData.currentStatus.kennelStatusNorm))}>
                            {formatKennelStatus(drawerAnimalData.currentStatus.kennelStatusNorm, drawerAnimalData.currentStatus.substatusNorm)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* This Interaction Section - Gate I17: Editable fields */}
                  <div data-testid="pending-interaction-details">
                    <h3 className="font-semibold mb-3">This Interaction</h3>
                    <div className="space-y-4 text-sm">
                      {/* Score Code - Quick-select chips (F-08) */}
                      <div className="flex items-start gap-2">
                        <AlertTriangleIcon className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <span className="font-medium min-w-[50px]">Score:</span>
                        <div className="flex flex-wrap gap-1 flex-1">
                          {SCORE_CODE_OPTIONS.map((opt) => {
                            const currentScore = selectedPendingInteraction.scoreCode || '';
                            const isSelected = currentScore === opt.value;
                            const band = getScoreBandFromCode(opt.value);
                            return (
                              <Button
                                key={opt.value}
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "toggle-elevate",
                                  isSelected && "toggle-elevated",
                                  isSelected && band === "GREEN" && "bg-success/15 text-success border-success/30",
                                  isSelected && band === "YELLOW" && "bg-warning/15 text-warning border-warning/30",
                                  isSelected && band === "RED" && "bg-destructive/15 text-destructive border-destructive/30",
                                )}
                                disabled={editMutation.isPending}
                                onClick={() => {
                                  if (isSelected || selectedInteractionIndex === null) return;
                                  editMutation.mutate({
                                    field: 'interactions.scoreCode',
                                    itemIndex: selectedInteractionIndex,
                                    newValue: opt.value,
                                  }, {
                                    onSuccess: () => {
                                      if (selectedPendingInteraction) {
                                        setSelectedPendingInteraction({
                                          ...selectedPendingInteraction,
                                          scoreCode: opt.value,
                                        });
                                      }
                                    },
                                  });
                                }}
                                data-testid={`button-score-chip-${opt.value}`}
                              >
                                {opt.value === "NONE" ? "None" : opt.label}
                              </Button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Author - Editable */}
                      <div className="flex items-start gap-2">
                        <User className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <span className="font-medium min-w-[50px]">Author:</span>
                        {drawerEditingField === 'author' ? (
                          <div className="flex items-center gap-1 flex-1">
                            <Input
                              value={drawerEditValue}
                              onChange={(e) => setDrawerEditValue(e.target.value)}
                              className="h-8 flex-1"
                              data-testid="input-drawer-author"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleDrawerFieldSave();
                                if (e.key === "Escape") handleDrawerFieldCancel();
                              }}
                            />
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={handleDrawerFieldSave}
                              disabled={editMutation.isPending}
                              data-testid="button-save-drawer-author"
                            >
                              {editMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={handleDrawerFieldCancel}
                              data-testid="button-cancel-drawer-author"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 flex-1">
                            <span>{selectedPendingInteraction.author || <span className="text-muted-foreground italic">Unknown</span>}</span>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 ml-auto"
                              onClick={() => handleDrawerFieldEdit('author', selectedPendingInteraction.author || '')}
                              data-testid="button-edit-drawer-author"
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Raw Note - Editable */}
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">Note:</span>
                          {drawerEditingField !== 'rawNote' && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              onClick={() => handleDrawerFieldEdit('rawNote', selectedPendingInteraction.rawNote || '')}
                              data-testid="button-edit-drawer-rawNote"
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        {drawerEditingField === 'rawNote' ? (
                          <div className="space-y-2">
                            <Textarea
                              value={drawerEditValue}
                              onChange={(e) => setDrawerEditValue(e.target.value)}
                              className="min-h-[120px] text-sm"
                              maxLength={10000}
                              data-testid="textarea-drawer-rawNote"
                              autoFocus
                            />
                            <div className="flex items-center gap-2 justify-end">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleDrawerFieldCancel}
                                data-testid="button-cancel-drawer-rawNote"
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                onClick={handleDrawerFieldSave}
                                disabled={editMutation.isPending}
                                data-testid="button-save-drawer-rawNote"
                              >
                                {editMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                                Save
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="p-3 bg-muted/50 rounded-md" data-testid="pending-interaction-note-content">
                            {selectedPendingInteraction.rawNote ? (
                              <InteractionNoteRenderer
                                note={selectedPendingInteraction.rawNote}
                                showNumberHighlights
                              />
                            ) : (
                              <span className="text-sm text-muted-foreground italic">No note</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Gate I17: Unsaved changes warning dialog */}
      <AlertDialog open={showDrawerUnsavedWarning} onOpenChange={setShowDrawerUnsavedWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. What would you like to do?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setShowDrawerUnsavedWarning(false);
                setPendingDrawerAction(null);
              }}
              data-testid="button-drawer-unsaved-cancel"
            >
              Continue Editing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDrawerDiscardAndContinue}
              data-testid="button-drawer-unsaved-discard"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditableCell({
  value,
  isEditing,
  editValue,
  onEdit,
  onChange,
  onSave,
  onCancel,
  isPending,
  testIdPrefix,
  hasError,
  errorVariant = "destructive",
}: {
  value: string;
  isEditing: boolean;
  editValue: string;
  onEdit: () => void;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isPending: boolean;
  testIdPrefix: string;
  hasError?: boolean;
  errorVariant?: "destructive" | "warning";
}) {
  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          value={editValue}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-full min-w-[100px]"
          data-testid={`input-${testIdPrefix}`}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") onSave();
            if (e.key === "Escape") onCancel();
          }}
        />
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 flex-shrink-0"
          onClick={onSave}
          disabled={isPending}
          data-testid={`button-save-${testIdPrefix}`}
        >
          {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 flex-shrink-0"
          onClick={onCancel}
          data-testid={`button-cancel-${testIdPrefix}`}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  const errorColorClass = errorVariant === "warning" ? "text-warning" : "text-destructive";

  return (
    <div className="flex items-center gap-1 group">
      {hasError && <AlertTriangle className={cn("h-3.5 w-3.5 flex-shrink-0", errorColorClass)} />}
      <span className={cn("line-clamp-1 break-all", hasError && cn(errorColorClass, "font-medium"))}>{value || "—"}</span>
      <Button
        size="icon"
        variant="ghost"
        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        onClick={onEdit}
        data-testid={`button-edit-${testIdPrefix}`}
      >
        <Pencil className="h-3 w-3" />
      </Button>
    </div>
  );
}

function EditableSelectCell({
  value,
  isEditing,
  editValue,
  options,
  onEdit,
  onChange,
  onSave,
  onCancel,
  isPending,
  testIdPrefix,
  placeholder,
  hasError,
  errorVariant = "destructive",
}: {
  value: string;
  isEditing: boolean;
  editValue: string;
  options: Array<{ value: string; label: string }>;
  onEdit: () => void;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isPending: boolean;
  testIdPrefix: string;
  placeholder?: string;
  hasError?: boolean;
  errorVariant?: "destructive" | "warning";
}) {
  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <Select
          value={editValue}
          onValueChange={(v) => {
            onChange(v);
          }}
        >
          <SelectTrigger 
            className="h-8 min-w-[100px]" 
            data-testid={`select-${testIdPrefix}`}
          >
            <SelectValue placeholder={placeholder || "Select..."} />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 flex-shrink-0"
          onClick={onSave}
          disabled={isPending}
          data-testid={`button-save-${testIdPrefix}`}
        >
          {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 flex-shrink-0"
          onClick={onCancel}
          data-testid={`button-cancel-${testIdPrefix}`}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  const displayLabel = options.find(o => o.value === value)?.label || value || "—";
  const errorColorClass = errorVariant === "warning" ? "text-warning" : "text-destructive";
  
  return (
    <div className="flex items-center gap-1 group">
      {hasError && <AlertTriangle className={cn("h-3.5 w-3.5 flex-shrink-0", errorColorClass)} />}
      <span className={cn("truncate", hasError && cn(errorColorClass, "font-medium"))}>{displayLabel}</span>
      <Button
        size="icon"
        variant="ghost"
        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        onClick={onEdit}
        data-testid={`button-edit-${testIdPrefix}`}
      >
        <Pencil className="h-3 w-3" />
      </Button>
    </div>
  );
}

const SPECIES_OPTIONS = [
  { value: "DOG", label: "Dog" },
  { value: "CAT", label: "Cat" },
];

const STATUS_CODE_OPTIONS = [
  { value: "AVAILABLE", label: "Available" },
  { value: "UNAVAIL", label: "Unavailable" },
  { value: "STRAY_WAIT", label: "Stray Wait" },
  { value: "EVALUATION", label: "Evaluation" },
];

const SCORE_CODE_OPTIONS = [
  { value: "NONE", label: "Confirmed — No Score" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3A", label: "3A" },
  { value: "3B", label: "3B" },
  { value: "4A", label: "4A" },
  { value: "4B", label: "4B" },
  { value: "5A", label: "5A" },
  { value: "5B", label: "5B" },
];
